import { AlertCircle, Check, Link as LinkIcon } from "lucide-react";
import React from "react";
import { copyMrLink } from "../utils";
import { Tooltip, TooltipBadgeContent } from "./Tooltip";

// How long the "copied"/"failed" feedback state stays visible (ms).
const FEEDBACK_DURATION_MS = 1500;

// Button that copies an MR as a rich-text hyperlink to the clipboard.
// Pastes into Google Docs (and similar editors) as a clickable link whose
// display text is "repo #iid: title".
export const CopyLinkButton = ({ mr, className = "" }) => {
	// "idle" | "copied" | "error"
	const [status, setStatus] = React.useState("idle");
	const timeoutRef = React.useRef(null);

	// Clear any pending feedback timer on unmount to avoid setting state on an
	// unmounted component.
	React.useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	const handleClick = async (e) => {
		// Avoid triggering drag/navigation on the parent card.
		e.preventDefault();
		e.stopPropagation();

		const ok = await copyMrLink(mr);
		setStatus(ok ? "copied" : "error");

		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
		timeoutRef.current = setTimeout(() => {
			setStatus("idle");
			timeoutRef.current = null;
		}, FEEDBACK_DURATION_MS);
	};

	const tooltip =
		status === "copied"
			? { message: "Copied MR link.", badge: "Done" }
			: status === "error"
				? { message: "Copy failed.", badge: "Error" }
				: { message: "Copy a rich MR link.", badge: "Action" };

	return (
		<Tooltip
			content={
				<TooltipBadgeContent badge={tooltip.badge}>
					{tooltip.message}
				</TooltipBadgeContent>
			}
		>
			<button
				type="button"
				onClick={handleClick}
				draggable="false"
				aria-label="Copy link to merge request"
				className={`inline-flex h-6 w-6 items-center justify-center rounded hover:bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] hover:text-[var(--color-link)] transition-colors ${className}`}
			>
				{status === "copied" ? (
					<Check className="w-4 h-4 text-green-500 animate-in zoom-in-50 spin-in-12 duration-200" />
				) : status === "error" ? (
					<AlertCircle className="w-4 h-4 text-red-500 animate-in zoom-in-50 duration-200" />
				) : (
					<LinkIcon className="w-4 h-4" />
				)}
			</button>
		</Tooltip>
	);
};

export default CopyLinkButton;
